import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Result, Button, Spin, Alert, Space } from 'antd';
import { ReloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

interface BackendStatus {
  status: 'healthy' | 'unhealthy' | 'unavailable';
  timestamp?: number;
  version?: string;
  uptime?: string;
  services?: Record<string, { status: string; response_time?: string; error?: string }>;
  error?: string;
}

interface BackendStatusContextType {
  backendStatus: BackendStatus | null | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  isBackendAvailable: boolean;
}

const BackendStatusContext = createContext<BackendStatusContextType | null>(null);

export const useBackendStatus = () => {
  const context = useContext(BackendStatusContext);
  if (!context) {
    throw new Error('useBackendStatus must be used within BackendStatusProvider');
  }
  return context;
};

interface BackendStatusProviderProps {
  children: ReactNode;
}

export const BackendStatusProvider: React.FC<BackendStatusProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [startTime] = useState(() => Date.now());
  const MAX_WAIT_TIME = 60000; // 60 секунд максимальное время ожидания

  // Проверяем статус backend каждые 30 секунд
  const {
    data: backendStatus,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['backend-status'],
    queryFn: async (): Promise<BackendStatus> => {
      // Проверяем максимальный таймаут (безопасность)
      const elapsed = Date.now() - startTime;
      if (elapsed > MAX_WAIT_TIME) {
        throw new Error(`Превышено максимальное время ожидания (${MAX_WAIT_TIME / 1000} секунд). Backend не запустился.`);
      }

      try {
        // Добавляем таймаут для health-check запроса (безопасность)
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 секунд таймаут для одного запроса

        const response = await fetch('/api/v1/health', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);


        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const payload = await response.json();


        return payload;
      } catch (caughtError) {
        const message = caughtError instanceof Error ? caughtError.message : String(caughtError);
        const isTimeout = caughtError instanceof Error && (caughtError.name === 'AbortError' || message.includes('timeout'));
        const elapsed = Date.now() - startTime;
        

        // Если превышен максимальный таймаут - выбрасываем ошибку
        if (elapsed > MAX_WAIT_TIME) {
          throw new Error(`Превышено максимальное время ожидания (${MAX_WAIT_TIME / 1000} секунд). Backend не запустился.`);
        }

        throw caughtError;
      }
    },
    refetchInterval: 30000, // Проверяем каждые 30 секунд
    retry: 10, // Увеличиваем количество попыток для ожидания запуска backend
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Экспоненциальная задержка до 10 секунд
    staleTime: 10000, // Считаем данные свежими 10 секунд
    gcTime: 60000, // Время кеширования (было cacheTime)
  });

  const isBackendAvailable = backendStatus?.status === 'healthy';
  
  // Определяем, показывать ли индикатор ожидания вместо ошибки (UX улучшение)
  const isWaitingForStartup = Boolean(isLoading || (error && (error.message === 'Failed to fetch' || error.message.includes('connectionfailure'))));
  const elapsed = Date.now() - startTime;
  const isMaxTimeoutReached = elapsed > MAX_WAIT_TIME;

  const contextValue: BackendStatusContextType = {
    backendStatus,
    isLoading,
    error: error as Error | null,
    refetch,
    isBackendAvailable,
  };

  // Если идет загрузка или backend недоступен - показываем страницу-заглушку
  if (isLoading || !isBackendAvailable) {
    return (
      <BackendOfflinePage
        backendStatus={backendStatus}
        isLoading={isLoading}
        error={error as Error | null}
        onRetry={refetch}
        isWaitingForStartup={isWaitingForStartup && !isMaxTimeoutReached}
        elapsed={elapsed}
        maxWaitTime={MAX_WAIT_TIME}
      />
    );
  }

  return (
    <BackendStatusContext.Provider value={contextValue}>
      {children}
    </BackendStatusContext.Provider>
  );
};

interface BackendOfflinePageProps {
  backendStatus: BackendStatus | undefined;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  isWaitingForStartup?: boolean;
  elapsed?: number;
  maxWaitTime?: number;
}

const BackendOfflinePage: React.FC<BackendOfflinePageProps> = ({
  backendStatus,
  isLoading,
  error,
  onRetry,
  isWaitingForStartup = false,
  elapsed = 0,
  maxWaitTime = 60000,
}) => {
  const { t } = useTranslation();
  const remainingSeconds = Math.max(0, Math.floor((maxWaitTime - elapsed) / 1000));

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
      }}
    >
      <div style={{ maxWidth: 600, width: '100%', padding: 24 }}>
        {isLoading || isWaitingForStartup ? (
          <div style={{ textAlign: 'center' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: '#666' }}>
              {isWaitingForStartup
                ? t('common.waiting_for_server_start', 'Waiting for server to start...')
                : t('common.checking_server_connection', 'Checking server connection...')}
            </div>
            {isWaitingForStartup && remainingSeconds > 0 && (
              <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
                {t('common.time_remaining', 'Time remaining: {{seconds}} sec', { seconds: remainingSeconds })}
              </div>
            )}
          </div>
        ) : (
          <Result
            status="warning"
            icon={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
            title={t('Сервер недоступен', 'Server Unavailable')}
            subTitle={
              <div>
                <div style={{ marginBottom: 16 }}>
                  {t(
                    'Не удается подключиться к серверу WorkerNet. Попробуйте обновить страницу через несколько минут.',
                    'Unable to connect to WorkerNet server. Please try refreshing the page in a few minutes.'
                  )}
                </div>

                {backendStatus && (
                  <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                    <Alert
                      message={t('Статус сервера', 'Server Status')}
                      description={
                        <div>
                          <div>
                            <strong>{t('Backend', 'Backend')}:</strong>{' '}
                            <span style={{ color: backendStatus.status === 'healthy' ? 'green' : 'red' }}>
                              {backendStatus.status === 'healthy'
                                ? t('Работает', 'Working')
                                : t('Недоступен', 'Unavailable')}
                            </span>
                          </div>
                          {backendStatus.uptime && (
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              Uptime: {backendStatus.uptime}
                            </div>
                          )}
                          {backendStatus.error && (
                            <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                              <strong>{t('Ошибка', 'Error')}:</strong> {backendStatus.error}
                            </div>
                          )}
                        </div>
                      }
                      type={backendStatus.status === 'healthy' ? 'success' : 'warning'}
                      showIcon
                    />
                  </Space>
                )}

                {error && (
                  <Alert
                    message={t('Ошибка подключения', 'Connection Error')}
                    description={
                      error.message === 'Failed to fetch' || error.message.includes('connectionfailure')
                        ? t('Не удалось подключиться к серверу. Проверьте, что backend сервисы запущены.', 'Failed to connect to server. Please check that backend services are running.')
                        : error.message.includes('timeout')
                        ? t('Превышено время ожидания ответа от сервера. Сервер может быть перегружен.', 'Server response timeout. The server may be overloaded.')
                        : error.message.includes('ECONNREFUSED')
                        ? t('Соединение отклонено. Убедитесь, что backend сервисы запущены на порту 8081.', 'Connection refused. Make sure backend services are running on port 8081.')
                        : error.message
                    }
                    type="error"
                    showIcon
                    style={{ marginTop: 16 }}
                  />
                )}
              </div>
            }
            extra={
              <Space>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={onRetry}
                  loading={isLoading}
                >
                  {t('Повторить проверку', 'Retry Check')}
                </Button>
                <Button
                  onClick={() => window.location.reload()}
                >
                  {t('Обновить страницу', 'Refresh Page')}
                </Button>
              </Space>
            }
          />
        )}
      </div>
    </div>
  );
};